import React, { useCallback, useEffect, useRef } from "react";

const CANVAS_WIDTH = 560;
const CANVAS_HEIGHT = 200;

interface SignaturePadCanvasProps {
  initialImage?: string;
  disabled?: boolean;
}

export interface SignaturePadCanvasHandle {
  clear: () => void;
  isEmpty: () => boolean;
  toDataUrl: () => string;
}

function initCanvas(canvas: HTMLCanvasElement): CanvasRenderingContext2D {
  const ctx = canvas.getContext("2d");
  if (!ctx) {
    throw new Error("Canvas context unavailable");
  }

  const dpr = window.devicePixelRatio || 1;
  canvas.width = CANVAS_WIDTH * dpr;
  canvas.height = CANVAS_HEIGHT * dpr;
  canvas.style.width = `${CANVAS_WIDTH}px`;
  canvas.style.height = `${CANVAS_HEIGHT}px`;
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  ctx.scale(dpr, dpr);
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);
  ctx.strokeStyle = "#111827";
  ctx.lineWidth = 2.5;
  ctx.lineCap = "round";
  ctx.lineJoin = "round";

  return ctx;
}

function loadImageOnCanvas(
  canvas: HTMLCanvasElement,
  imageSrc: string
): Promise<void> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const ctx = initCanvas(canvas);
      ctx.drawImage(img, 0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);
      resolve();
    };
    img.onerror = () => reject(new Error("Failed to load signature image"));
    img.src = imageSrc;
  });
}

export const SignaturePadCanvas = React.forwardRef<
  SignaturePadCanvasHandle,
  SignaturePadCanvasProps
>(function SignaturePadCanvas({ initialImage, disabled = false }, ref) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const ctxRef = useRef<CanvasRenderingContext2D | null>(null);
  const drawingRef = useRef(false);
  const hasStrokesRef = useRef(false);
  const lastPointRef = useRef<{ x: number; y: number } | null>(null);

  const getPoint = useCallback(
    (event: React.MouseEvent | React.TouchEvent): { x: number; y: number } => {
      const canvas = canvasRef.current;
      if (!canvas) return { x: 0, y: 0 };

      const rect = canvas.getBoundingClientRect();
      const clientX =
        "touches" in event ? event.touches[0]?.clientX ?? 0 : event.clientX;
      const clientY =
        "touches" in event ? event.touches[0]?.clientY ?? 0 : event.clientY;

      return {
        x: clientX - rect.left,
        y: clientY - rect.top,
      };
    },
    []
  );

  const clearCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    ctxRef.current = initCanvas(canvas);
    hasStrokesRef.current = false;
    lastPointRef.current = null;
  }, []);

  const isEmpty = useCallback(() => !hasStrokesRef.current, []);

  const toDataUrl = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return "";
    return canvas.toDataURL("image/png");
  }, []);

  React.useImperativeHandle(ref, () => ({
    clear: clearCanvas,
    isEmpty,
    toDataUrl,
  }));

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    let cancelled = false;

    const setup = async () => {
      if (initialImage) {
        try {
          await loadImageOnCanvas(canvas, initialImage);
          if (!cancelled) {
            ctxRef.current = canvas.getContext("2d");
            hasStrokesRef.current = true;
          }
        } catch {
          if (!cancelled) clearCanvas();
        }
      } else {
        clearCanvas();
      }
    };

    setup();

    return () => {
      cancelled = true;
    };
  }, [initialImage, clearCanvas]);

  const startDrawing = (event: React.MouseEvent | React.TouchEvent) => {
    if (disabled) return;
    if ("touches" in event) event.preventDefault();

    const ctx = ctxRef.current;
    if (!ctx) return;

    drawingRef.current = true;
    hasStrokesRef.current = true;
    lastPointRef.current = getPoint(event);
  };

  const draw = (event: React.MouseEvent | React.TouchEvent) => {
    if (!drawingRef.current || disabled) return;
    if ("touches" in event) event.preventDefault();

    const ctx = ctxRef.current;
    const lastPoint = lastPointRef.current;
    if (!ctx || !lastPoint) return;

    const point = getPoint(event);
    ctx.beginPath();
    ctx.moveTo(lastPoint.x, lastPoint.y);
    ctx.lineTo(point.x, point.y);
    ctx.stroke();
    lastPointRef.current = point;
  };

  const stopDrawing = () => {
    drawingRef.current = false;
    lastPointRef.current = null;
  };

  return (
    <div className="w-full">
      <div className="rounded-xl border-2 border-dashed border-gray-300 bg-white overflow-hidden touch-none">
        <canvas
          ref={canvasRef}
          className="block w-full cursor-crosshair"
          onMouseDown={startDrawing}
          onMouseMove={draw}
          onMouseUp={stopDrawing}
          onMouseLeave={stopDrawing}
          onTouchStart={startDrawing}
          onTouchMove={draw}
          onTouchEnd={stopDrawing}
          aria-label="Área para desenhar assinatura"
        />
      </div>
      <p className="mt-2 text-xs text-gray-500">
        Desenhe sua assinatura com o mouse ou o dedo
      </p>
    </div>
  );
});
